import { buttonStyle } from 'components/buttons/buttonStyle';
import PermissionButton from 'components/buttons/PermissionButton';
import { useInCreateDialogSections } from 'components/create-dialog/CreateDialogRequired';
import FeatureFlagged from 'components/featureflag/FeatureFlagged';
import ContactPickerModal from 'components/forms/form-components/select-picker/ContactPickerModal';
import CheckboxInput from 'components/forms/inputs/rhf-inputs/CheckboxInput.tsx';
import DateInput from 'components/forms/inputs/rhf-inputs/DateInput';
import InputWrapper from 'components/forms/inputs/rhf-inputs/InputWrapper';
import MultiSelectInput from 'components/forms/inputs/rhf-inputs/MultiSelectInput';
import NumberInput from 'components/forms/inputs/rhf-inputs/NumberInput';
import PercentageInput from 'components/forms/inputs/rhf-inputs/PercentageInput';
import SelectInput from 'components/forms/inputs/rhf-inputs/SelectInput';
import TextAreaInput from 'components/forms/inputs/rhf-inputs/TextAreaInput';
import TextInput from 'components/forms/inputs/rhf-inputs/TextInput';
import { useMemoizedRules } from 'components/forms/inputs/rhf-inputs/useMemoizedRules';
import InfoTooltip from 'components/InfoTooltip';
import { useModal } from 'components/modals/useModal';
import ContactDynamicMultiSelectPicker from 'components/party/ContactDynamicMultiSelectPicker';
import ContactDynamicSelectPicker from 'components/party/ContactDynamicSelectPicker';
import { useInRecordSection } from 'components/section-edit/SectionEdit';
import { Button } from 'components/ui/button';
import { Label } from 'components/ui/label';
import { RadioGroup, RadioGroupItem } from 'components/ui/radio-group';
import { EditHourlyRatesModal } from 'pages/pricelists/modal/EditHourlyRatesModal';
import { PricelistDetailsCard } from 'pages/pricelists/PricelistDetailsCard';
import { useCallback, useState } from 'react';
import { Control, Controller, useWatch } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useLazyGetPricelistByIdQuery } from 'store/api/pricelists';
import {
    useLazyGetMinContactsListWithFiltersQuery,
    useLazyGetMinContactsWithRelationsListWithFiltersQuery,
} from 'store/api/contacts';
import { ClassifierData } from 'types/classifiers';
import { ContactMinDtoWithRelationsDto, PartyGroupingStrategy, PartyType } from 'types/contacts';
import { ContractForm } from 'types/contracts';
import { FeatureFlag } from 'types/features';
import { UpdateHourlyRatesDto } from 'types/matters';
import { copiedPeriods, editorPricingKind } from 'pages/pricelists/modal/employeePricing';
import { Pricelist, PricingKind, StagedPricelistPeriod } from 'types/pricelists';
import { cn } from 'utils/cn';
import { getValidOptionsFromClassifier } from 'utils/formUtil';
import { t } from 'utils/getTranslations';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags.ts';
import { FollowedPricelist, ModelPricelistOptions } from '../ModelPricelistOptions';
import { ContractFormState } from './useContractForm';

type Option = { label: string; value: string };

const ADD_CONTACT_PERMISSIONS = ['CREATE.PARTIES', 'CREATE.PARTIES.CLIENTS', 'CREATE.PARTIES.LEADS_AND_NON_CLIENTS'];

const addNewContactButton = (form: ContractFormState) => (
    <PermissionButton
        permissions={ADD_CONTACT_PERMISSIONS}
        variant="contained"
        color="secondary"
        size="extraSmall"
        onClick={form.openAddNewContactModal}
    >
        <FormattedMessage id="contacts.add-new-contact" />
    </PermissionButton>
);

/**
 * General: the contract's type and template, its parties and companies, number, keywords and DMS link. With a
 * `part`, only the Contract section's fields or only the Parties section's; without one, all of them in the
 * form's order.
 */
export const GeneralFields = ({ form, part }: { form: ContractFormState; part?: 'contract' | 'parties' }) => {
    const { control, setValue } = form.methods;
    const {
        activeCompanyList,
        classifiers,
        contractTemplatesOptions,
        isCooperation,
        isServiceAgreement,
        prefillAppliedIn,
        selectedCompanyPartyIds,
        selectedPersonPartyIds,
        validUrl,
    } = form;
    const contract = !part || part === 'contract';
    const parties = !part || part === 'parties';
    const prioritizes = selectedPersonPartyIds.length || selectedCompanyPartyIds.length;

    return (
        <>
            {contract ? (
                <SelectInput
                    setValue={setValue}
                    control={control}
                    label="contracts.type"
                    placeholder="contracts.select-type"
                    name="contractType"
                    options={(getValidOptionsFromClassifier(classifiers['CONTRACT_TYPE']) as Option[]) ?? []}
                    getOptionLabel="label"
                    getOptionIdentifier="value"
                    required
                />
            ) : null}
            {contract && isServiceAgreement ? (
                <TextInput
                    name="nameExtension"
                    control={control}
                    label="contracts.name-extension"
                    placeholder="contracts.name-extension-placeholder"
                    tooltip={<InfoTooltip text="contracts.tooltips.name-extension" />}
                />
            ) : null}
            {contract ? (
                <FeatureFlagged action="show" flag={FeatureFlag.DOWNLOAD_CONTRACT_TEMPLATE}>
                    <>
                        <CheckboxInput control={control} name="isClientTemplate" label="contracts.client-template" />
                        <SelectInput
                            setValue={setValue}
                            control={control}
                            label="contracts.contract-template"
                            placeholder="contracts.select-template"
                            name="contractTemplate"
                            options={contractTemplatesOptions ?? []}
                            getOptionLabel="name"
                            getOptionIdentifier="id"
                        />
                    </>
                </FeatureFlagged>
            ) : null}
            {parties ? (
                <>
                    <ContactDynamicMultiSelectPicker
                        setValue={setValue}
                        control={control}
                        label="contracts.connected-contacts"
                        placeholder="contracts.select-contacts"
                        name="parties"
                        paginated={{
                            fetch: useLazyGetMinContactsListWithFiltersQuery,
                            searchField: ['name', 'partyNumber'],
                        }}
                        required
                        tooltip={<InfoTooltip text="contracts.tooltips.connected-contacts" />}
                        PickerModalComponent={ContactPickerModal}
                    />
                    <SelectInput
                        setValue={setValue}
                        control={control}
                        label="contracts.concluded-by"
                        placeholder="forms.select-company"
                        name="tenantCompany"
                        options={activeCompanyList}
                        getOptionLabel="name"
                        getOptionIdentifier="id"
                        onChange={(val) => prefillAppliedIn(val)}
                        required
                        tooltip={<InfoTooltip text="contracts.tooltips.concluded-by" />}
                    />
                    <MultiSelectInput
                        setValue={setValue}
                        control={control}
                        label="contracts.applied-in"
                        placeholder="contracts.select-companies"
                        name="companies"
                        options={activeCompanyList}
                        getOptionLabel="name"
                        getOptionIdentifier="id"
                        required
                        tooltip={<InfoTooltip text="contracts.tooltips.applied-in" />}
                    />
                    <ContactDynamicSelectPicker
                        setValue={setValue}
                        control={control}
                        label={
                            isCooperation ? 'contracts.signed-by-contact-person' : 'contracts.signed-by-client-contact'
                        }
                        placeholder="contracts.select-client-contact"
                        name="signedByPersonParty"
                        paginated={{
                            fetch: prioritizes
                                ? useLazyGetMinContactsWithRelationsListWithFiltersQuery
                                : useLazyGetMinContactsListWithFiltersQuery,
                            filters: `partyType==${PartyType.PERSON}`,
                            searchField: ['name', 'partyNumber'],
                            additionalParams: prioritizes
                                ? {
                                      ...(selectedPersonPartyIds.length
                                          ? { prioritizeParties: selectedPersonPartyIds }
                                          : undefined),
                                      ...(selectedCompanyPartyIds.length
                                          ? { prioritizeEmployeesOf: selectedCompanyPartyIds }
                                          : undefined),
                                      groupingStrategy: PartyGroupingStrategy.PARTIES_FIRST,
                                  }
                                : undefined,
                        }}
                        {...(prioritizes && {
                            groupByStaticFields: (option: ContactMinDtoWithRelationsDto) => {
                                const isContactRelated = selectedPersonPartyIds.includes(option.id);

                                const isCompanyRelated = option.relatesTo?.some((rel) =>
                                    selectedCompanyPartyIds.includes(rel.otherParty.id)
                                );

                                if (isContactRelated) {
                                    return t('contracts.groups.contact-related-people');
                                }

                                if (isCompanyRelated) {
                                    return t('contracts.groups.company-related-people');
                                }
                                return t('field.other-people');
                            },
                        })}
                        isPickerModalOptionEnabled={(option) => option.partyType === PartyType.PERSON}
                        PickerModalComponent={ContactPickerModal}
                        groupSortOrder={[
                            t('contracts.groups.company-related-people'),
                            t('contracts.groups.contact-related-people'),
                        ]}
                        defaultGroup={t('contracts.groups.all-contacts')}
                        noOptionsContent={addNewContactButton(form)}
                    />
                </>
            ) : null}
            {contract ? (
                <>
                    <TextInput
                        name="contractNumber"
                        control={control}
                        label="contracts.number"
                        placeholder="contracts.enter-number"
                        tooltip={<InfoTooltip text="contracts.tooltips.contract-number" />}
                    />
                    <MultiSelectInput
                        control={control}
                        setValue={setValue}
                        name="contractRelatedKeywords"
                        label="contracts.contract-related-keywords"
                        options={
                            (getValidOptionsFromClassifier(classifiers['CONTRACT_RELATED_KEYWORD_TYPE']) as {
                                label: string;
                                value: string | number;
                            }[]) ?? []
                        }
                        getOptionIdentifier="value"
                        getOptionLabel="label"
                    />
                    <TextInput name="dmsLink" control={control} label="contracts.dms-link" validate={validUrl} />
                </>
            ) : null}
        </>
    );
};

/** Ultimate beneficial owners, for AML and UBO contracts. */
export const BeneficialOwnersFields = ({ form }: { form: ContractFormState }) => {
    const { control, setValue } = form.methods;
    return (
        <>
            <ContactDynamicMultiSelectPicker
                setValue={setValue}
                control={control}
                label="contracts.ultimate-beneficial-owners"
                placeholder="contracts.select-client-contact"
                name="beneficialOwners"
                paginated={{
                    fetch: useLazyGetMinContactsListWithFiltersQuery,
                    filters: `partyType==${PartyType.PERSON}`,
                    searchField: ['name', 'partyNumber'],
                }}
                isPickerModalOptionEnabled={(option) => option.partyType === PartyType.PERSON}
                groupSortOrder={[
                    t('contracts.groups.company-related-people'),
                    t('contracts.groups.contact-related-people'),
                ]}
                defaultGroup={t('contracts.groups.all-contacts')}
                noOptionsContent={addNewContactButton(form)}
                PickerModalComponent={ContactPickerModal}
            />
            <TextAreaInput
                name="beneficialOwnerNotes"
                control={control}
                label="contracts.beneficial-owner-notes"
                placeholder="contracts.beneficial-owner-notes"
                enableAutofill
            />
        </>
    );
};

/** What the Financial section holds besides the price list and financial conditions. */
export type FinancialPart = 'pricing' | 'terms' | 'increment' | 'bank-account';

/**
 * Financial: the price list and financial conditions (`pricing`), the type's terms — timecard increment, admin
 * fee, discount — (`terms`), the timecard increment alone (`increment`) and the bank account. With a `part`, only
 * those fields; without one, all of them in the form's order.
 */
export const FinancialFields = ({ form, part }: { form: ContractFormState; part?: FinancialPart }) => {
    const { control, setValue } = form.methods;
    const {
        classifiers,
        currencyType,
        hasAdminFeePermitted,
        ibans,
        isServiceAgreement,
        isTimecardIncrementControlEnabled,
        onNewPricelistFormSubmit,
        onNewEmployeePricelistSubmit,
        onFollowPricelist,
        timecardIncrementOptions,
    } = form;
    const pricing = !part || part === 'pricing';
    const terms = !part || part === 'terms';

    return (
        <>
            {pricing ? (
                <>
                    <SelectInput
                        setValue={setValue}
                        control={control}
                        label="currency"
                        placeholder="forms.select-currency"
                        name="currencyType"
                        options={(getValidOptionsFromClassifier(classifiers['CURRENCY']) as Option[]) ?? []}
                        getOptionLabel="label"
                        getOptionIdentifier="value"
                        required
                    />
                    <PriceListInput
                        control={control}
                        label="matters.pricelist"
                        onSumbitNew={onNewPricelistFormSubmit}
                        onSubmitEmployeePeriods={onNewEmployeePricelistSubmit}
                        onFollow={onFollowPricelist}
                        classifiers={classifiers}
                        disabled={!currencyType?.value}
                    />
                </>
            ) : null}
            {(terms || part === 'increment') && isServiceAgreement && isTimecardIncrementControlEnabled ? (
                <SelectInput
                    setValue={setValue}
                    control={control}
                    label="contracts.timecard-increment"
                    placeholder="contracts.select-timecard-increment"
                    name="timecardIncrement"
                    options={timecardIncrementOptions}
                    getOptionLabel="label"
                    getOptionIdentifier="value"
                    required
                />
            ) : null}
            {terms && hasAdminFeePermitted ? (
                <PercentageInput
                    name="adminFeePercentage"
                    control={control}
                    label="contracts.admin-fee-percentage"
                    placeholder="contracts.admin-fee-percentage"
                    decimalPlaces={2}
                    maxWholeDigits={4}
                    inputProps={{ min: 0 }}
                    required
                />
            ) : null}
            {pricing ? (
                <>
                    <NumberInput
                        name="paymentDeadlineInDays"
                        control={control}
                        label="financial-conditions.payment-deadline"
                        placeholder="forms.enter-payment-deadline"
                        required
                        inputProps={{ min: 0, max: 120 }}
                        rightLabelComponent={
                            <InfoTooltip text={'financial-conditions.tooltips.eu-late-payment-directive'} />
                        }
                    />
                    <NumberInput
                        name="prepaymentPaymentDeadlineInDays"
                        control={control}
                        label="financial-conditions.prepayment-payment-deadline"
                        placeholder="forms.enter-payment-deadline"
                        inputProps={{ min: 0, max: 120 }}
                    />
                    <SelectInput
                        setValue={setValue}
                        control={control}
                        label="financial-conditions.invoicing-interval"
                        placeholder="forms.select-invoicing-interval"
                        name="invoicingIntervalType"
                        options={
                            (getValidOptionsFromClassifier(classifiers['INVOICING_INTERVAL_TYPE']) as Option[]) ?? []
                        }
                        getOptionLabel="label"
                        getOptionIdentifier="value"
                        required
                    />
                </>
            ) : null}
            {terms ? (
                <PercentageInput
                    control={control}
                    name="discountPct"
                    label="matters.discount"
                    placeholder="matters.discount"
                />
            ) : null}
            {pricing ? (
                <>
                    <PercentageInput
                        name="interestRatePct"
                        control={control}
                        label="financial-conditions.interest-rate"
                        placeholder="forms.enter-interest-rate"
                        required
                        inputProps={{ max: 99 }}
                    />
                    <PercentageInput
                        name="interestRateB2cPct"
                        control={control}
                        label="financial-conditions.interest-rate-b2c"
                        placeholder="forms.enter-interest-rate-b2c"
                        required
                        inputProps={{ max: 99 }}
                    />
                </>
            ) : null}
            {!part || part === 'bank-account' ? (
                <SelectInput
                    setValue={setValue}
                    control={control}
                    label="contracts.bank-account"
                    name="bankAccount"
                    options={ibans}
                    getOptionLabel="name"
                    getOptionIdentifier="id"
                    placeholder="contacts.select-bank-account"
                />
            ) : null}
        </>
    );
};

/** Billing: billing notes, internal invoicing info and the client's reference. */
export const BillingFields = ({ form }: { form: ContractFormState }) => {
    const { control } = form.methods;
    return (
        <>
            <TextAreaInput
                name="billingNotes"
                control={control}
                label="contracts.billing-notes"
                placeholder="contracts.billing-notes"
                enableAutofill
            />
            <FeatureFlagged action="show" flag={FeatureFlag.INVOICING_INFO_ENABLED}>
                <TextAreaInput
                    name="invoicingInfo"
                    control={control}
                    label="contracts.invoicing-info-internal"
                    placeholder="contracts.invoicing-info-internal"
                    enableAutofill
                />
            </FeatureFlagged>
            <TextInput
                name="clientReference"
                control={control}
                label="contracts.client-reference"
                placeholder="contracts.enter-client-reference"
                tooltip={<InfoTooltip text="contracts.tooltips.client-reference" />}
            />
        </>
    );
};

/** Dates: sent, signed and terminated; the creation date, read only, on an existing contract. */
export const DatesFields = ({ form }: { form: ContractFormState }) => {
    const { control } = form.methods;
    // On the record page the creation date stays the read view's row, under the dates a field edits.
    const inSection = useInRecordSection();
    return (
        <>
            {form.type === 'edit' && !inSection ? (
                <DateInput control={control} name="readOnlyCreatedAt" label="contracts.created" readonly />
            ) : null}
            <DateInput control={control} name="contractSentAt" label="contracts.sent" />
            <DateInput control={control} name="contractSignedAt" label="contracts.signed" />
            <DateInput control={control} name="contractTerminatedAt" label="contracts.terminated" />
        </>
    );
};

export const NotesFields = ({ form }: { form: ContractFormState }) => (
    <TextAreaInput
        control={form.methods.control}
        label="contracts.additional-notes"
        placeholder="contracts.additional-notes"
        name="additionalNotes"
        enableAutofill
    />
);

interface PropsPriceListInput {
    label: string;
    control: Control<ContractForm>;
    classifiers: Record<string, ClassifierData>;
    onSumbitNew: (value: UpdateHourlyRatesDto, closeModal: () => void, currencyTypeCode: string | undefined) => void;
    onSubmitEmployeePeriods: (
        periods: StagedPricelistPeriod[],
        closeModal: () => void,
        currencyTypeCode: string | undefined
    ) => void;
    onFollow: (
        model: FollowedPricelist,
        periods: StagedPricelistPeriod[],
        currencyTypeCode: string | undefined
    ) => void;
    disabled: boolean;
}

const PriceListInput = ({
    control,
    label,
    classifiers,
    onSumbitNew,
    onSubmitEmployeePeriods,
    onFollow,
    disabled,
}: PropsPriceListInput) => {
    const { openModal, closeModal } = useModal();
    const inCreateDialog = useInCreateDialogSections();
    const isEmployeePricingEnabled = useIsFeatureEnabled(FeatureFlag.EMPLOYEE_PRICING);
    const [isChoosingModel, setIsChoosingModel] = useState(false);
    const [
        currencyType,
        pricelistId,
        pricelistRates,
        pricelist,
        parties,
        pricingHash,
        pricelistPeriods,
        pricelistFollows,
    ] = useWatch({
        control,
        name: [
            'currencyType',
            'pricelistId',
            'pricelistRates',
            'pricelist',
            'parties',
            'pricingHash',
            'pricelistPeriods',
            'pricelistFollows',
        ],
    });
    // A list keeps the kind it was stored or staged with; one not made yet takes the firm's pricing mode.
    const pricingKind = editorPricingKind(pricelist, !pricelistId && !pricelist?.pricingKind, isEmployeePricingEnabled);
    const isEmployeeMode = pricingKind === PricingKind.EMPLOYEE;
    // The role rows are what a role-priced list requires; an employee-priced one requires its periods, or a model
    // it follows, instead.
    const validateEmployeePeriods = useCallback(
        (_value: unknown, values: ContractForm) =>
            !isEmployeeMode ||
            (!!values.pricelist && (!!values.pricelistFollows || !!values.pricelistPeriods?.length)) ||
            t('validation.required-field'),
        [isEmployeeMode]
    );
    const rules = useMemoizedRules<ContractForm, 'pricelistRates'>({
        required: !isEmployeeMode,
        validate: validateEmployeePeriods,
    });

    // A currency change drops the list but not its staged periods or link, which then belong to no list.
    const stagedPeriods = pricelist ? (pricelistPeriods ?? []) : [];
    const follows = pricelist ? (pricelistFollows ?? null) : null;

    const openPriceListModal = useCallback((): void => {
        const MODAL_STYLE = { width: 800 };
        // Own prices started from a followed model are a copy: the model's period ids are not this list's.
        const ownPeriods = pricelistFollows
            ? (pricelistPeriods ?? []).map((period) => ({ ...period, id: null }))
            : pricelistPeriods;
        openModal(
            <EditHourlyRatesModal
                clientParty={parties?.length ? parties[0] : undefined}
                pricelistReference={{
                    id: pricelistId,
                    pricingHash,
                }}
                pricelistRates={pricelistRates}
                onClose={closeModal}
                onSubmit={(data) => onSumbitNew(data, closeModal, currencyType?.value)}
                classifiers={classifiers}
                currencyTypeCode={currencyType?.value}
                pricingKind={pricingKind}
                periods={pricelist ? ownPeriods : null}
                onSubmitPeriods={(periods) => onSubmitEmployeePeriods(periods, closeModal, currencyType?.value)}
            />,
            closeModal,
            MODAL_STYLE
        );
    }, [
        classifiers,
        closeModal,
        currencyType?.value,
        onSumbitNew,
        onSubmitEmployeePeriods,
        openModal,
        parties,
        pricelistId,
        pricelistRates,
        pricingHash,
        pricingKind,
        pricelist,
        pricelistPeriods,
        pricelistFollows,
    ]);

    const isEmpty = isEmployeeMode ? !follows && !stagedPeriods.length : !pricelistRates?.length;
    const choice = follows || isChoosingModel ? 'follow' : 'own';

    return (
        <Controller
            control={control}
            name={'pricelistRates'}
            rules={rules}
            render={({ fieldState: { error } }) => (
                <InputWrapper
                    name={'pricelistRates'}
                    label={label}
                    required={true}
                    // Empty counts in the create dialog's Required section only: a page does not highlight this cell.
                    isEmpty={inCreateDialog ? isEmpty : undefined}
                    hasErrors={!!error}
                >
                    <div>
                        {isEmployeeMode ? (
                            <RadioGroup
                                aria-label={t('contracts.pricing.choice')}
                                value={choice}
                                onValueChange={(value) => {
                                    setIsChoosingModel(value === 'follow');
                                    if (value === 'own') {
                                        openPriceListModal();
                                    }
                                }}
                                disabled={disabled}
                                className="mb-2 flex flex-wrap gap-x-4 gap-y-2"
                            >
                                <Label className="cursor-pointer font-normal">
                                    <RadioGroupItem value="follow" />
                                    {t('contracts.pricing.follow-model')}
                                </Label>
                                <Label className="cursor-pointer font-normal">
                                    <RadioGroupItem value="own" />
                                    {t('contracts.pricing.own-prices')}
                                </Label>
                            </RadioGroup>
                        ) : null}
                        {isEmployeeMode && choice === 'follow' ? (
                            <div className="mb-2">
                                <ModelToFollow
                                    currencyTypeCode={currencyType?.value}
                                    followedId={follows?.id ?? null}
                                    onFollow={(model, periods) => onFollow(model, periods, currencyType?.value)}
                                    disabled={disabled}
                                />
                            </div>
                        ) : null}
                        <div
                            className={cn(
                                'flex justify-between rounded-[5px] border pr-2.5 pl-3',
                                // As high as the controls beside it in the create dialog: 36px, 44 on its sheet.
                                inCreateDialog ? 'py-[5px] max-sm:py-[9px]' : 'py-2.5',
                                error ? 'border-error' : 'border-grey-300'
                            )}
                        >
                            <div className="ml-0.5">
                                <PricelistDetailsCard
                                    pricelist={
                                        {
                                            currencyTypeCode: pricelist?.currencyTypeCode || '',
                                            name: follows
                                                ? t('contracts.pricing.follows', { name: follows.name })
                                                : pricelist?.name || '',
                                            rates: isEmployeeMode ? [] : pricelistRates,
                                            pricingKind,
                                            periods: isEmployeeMode ? stagedPeriods : null,
                                            labelsList: undefined,
                                        } as unknown as Pricelist
                                    }
                                />
                            </div>
                            {isEmployeeMode && choice === 'follow' ? null : (
                                <Button
                                    {...buttonStyle({ variant: 'outlined', size: 'extraSmall' })}
                                    onClick={() => openPriceListModal()}
                                    disabled={disabled}
                                >
                                    <FormattedMessage id="edit" />
                                </Button>
                            )}
                        </div>
                    </div>
                </InputWrapper>
            )}
        />
    );
};

// The model lists to follow; a pick reads the model's periods, which the form shows until it is saved.
function ModelToFollow({
    currencyTypeCode,
    followedId,
    onFollow,
    disabled,
}: {
    currencyTypeCode?: string;
    followedId: number | null;
    onFollow: (model: FollowedPricelist, periods: StagedPricelistPeriod[]) => void;
    disabled: boolean;
}) {
    const [fetchPricelist, { isFetching }] = useLazyGetPricelistByIdQuery();
    const follow = async (model: FollowedPricelist) => {
        try {
            const detail = await fetchPricelist(model.id).unwrap();
            onFollow(model, copiedPeriods(detail.periods));
        } catch {
            // The API layer has already said the read failed; the list stays as it was.
        }
    };
    return (
        <ModelPricelistOptions
            currencyTypeCode={currencyTypeCode}
            selectedId={followedId}
            onPick={(model) => void follow(model)}
            disabled={disabled || isFetching}
        />
    );
}
